import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Paper,
    TextField,
    Typography
} from "@mui/material";
import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import IconButton from "@mui/material/IconButton";

import {AgGridReact} from "ag-grid-react";
import {ColDef, GetRowIdParams, GridOptions} from "ag-grid-enterprise";
import {FirstDataRenderedEvent, RowNode, SelectionChangedEvent} from "ag-grid-community";
import {CloseRounded, ReplayRounded} from "@mui/icons-material";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {useAgGridLocale} from "../../../../config/globalConfig";


const AdvancedDMSselectionModal = (props: any) => {
    const {t} = useTranslation(['salesmarketing']);
    const agGridLocale = useAgGridLocale();

    const gridRef = useRef<AgGridReact>(null);

    const [selectedItemsInternal, setSelectedItemsInternal] = useState(props.selectedItems);
    const [items, setItems] = useState(props.items);

    const [areas, setAreas] = useState<any[]>([]);
    const [countries, setCountries] = useState<any[]>([]);
    const [regions, setRegions] = useState<any[]>([]);
    const [provinces, setProvinces] = useState<any[]>([]);
    const [cities, setCities] = useState<any[]>([]);

    const [selectedFilter, setSelectedFilter] = useState<any>({
        area: [],
        country: [],
        region: [],
        province: [],
        city: []
    });

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 10 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    const sorter = (valueA: string, valueB: string, nodeA: RowNode, nodeB: RowNode, isDescending: boolean): number => {
        let isSelectedA = nodeA.isSelected();
        let isSelectedB = nodeB.isSelected();

        if ((isSelectedB && isSelectedA) || (!isSelectedB && !isSelectedA)) {
            if (valueA == valueB) return 0;
            return (valueA > valueB) ? 1 : -1;
        } else {
            if (isSelectedA && !isSelectedB)
                return isDescending ? 1 : -1;
            return isDescending ? -1 : 1;

        }
    }

    const columns: ColDef[] = [
        {
            field: "name",
            headerName: t('selectDms.name'),
            tooltipField: "name",
            unSortIcon: true,
            headerCheckboxSelection: true,
            checkboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true
        },
        {
            field: "country",
            headerName: t('selectDms.country'),
            tooltipField: "country",
            unSortIcon: true
        },
        ...(items.some((i: any) => i.region) ? [{
            field: "region",
            headerName: t('selectDms.region'),
            tooltipField: "region",
            unSortIcon: true,
            valueGetter: (params: any) => params.data.region || ""
        }] : []),
        ...(items.some((i: any) => i.province) ? [{
            field: "province",
            headerName: t('selectDms.province'),
            tooltipField: "province",
            unSortIcon: true,
            valueGetter: (params: any) => params.data.province || ""
        }] : []),
        {
            field: "city",
            headerName: t('selectDms.city'),
            tooltipField: "city",
            unSortIcon: true
        }
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        defaultColDef: {
            sortable: true,
            resizable: false,
            wrapHeaderText: false,
            comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
                return sorter(valueA, valueB, nodeA, nodeB, isDescending)
            }
        },
        columnDefs: columns,
        serverSideInfiniteScroll: true,
        localeText: agGridLocale
    };

    useEffect(() => {
        setSelectedItemsInternal(props.selectedItems)
    }, [props.selectedItems]);

    useEffect(() => {
        setItems(props.items.map((i: any) => {
            return {...i, region: i.region && i.region !== '-' && i.region.includes('|') ? i.region.split('|')[1] : ''}
        }));
        fillSelects(props.items);
    }, [props.items])

    const arraySort = (array: string[]): string[] => {
        return array.sort((a: string, b: string): number => {
            if (a < b)
                return -1;
            return 1
        });
    }

    function fillSelects(passedItems: any) {
        // @ts-ignore
        setAreas([...new Set<any>(arraySort(passedItems.map((i: any) => i.area?.split('|')[1]).filter((i: any) => i !== undefined)))]);
        // @ts-ignore
        setCountries([...new Set<any>(arraySort(passedItems.map((i: any) => i.country).filter((i: any) => i !== undefined)))]);
        // @ts-ignore
        setRegions([...new Set<any>(arraySort(passedItems.map((i: any) => i.region?.split('|')[1]).filter((i: any) => i !== undefined)))]);
        // @ts-ignore
        setProvinces([...new Set<any>(arraySort(passedItems.map((i: any) => i.province).filter((i: any) => i !== undefined)))]);
        // @ts-ignore
        setCities([...new Set<any>(arraySort(passedItems.map((i: any) => i.city).filter((i: any) => i !== undefined)))]);
    }

    useEffect(() => {
        gridRef.current?.api.onFilterChanged();
    }, [selectedFilter])

    const handleChangeSelectedAreas = (event: any, newVal: string[]) => {
        setSelectedFilter({...selectedFilter, area: newVal});
    };
    const handleChangeSelectedCountries = (event: any, newVal: string[]) => {
        setSelectedFilter({...selectedFilter, country: newVal});
    };
    const handleChangeSelectedRegions = (event: any, newVal: string[]) => {
        setSelectedFilter({...selectedFilter, region: newVal});
    };
    const handleChangeSelectedProvinces = (event: any, newVal: string[]) => {
        setSelectedFilter({...selectedFilter, province: newVal});
    };
    const handleChangeSelectedCities = (event: any, newVal: string[]) => {
        setSelectedFilter({...selectedFilter, city: newVal});
    };

    const onFirstDataRendered =
        (params: FirstDataRenderedEvent<any>) => {
            gridRef.current!.api.forEachNode((node) => {
                    node.setSelected(!!node.data && selectedItemsInternal.some((i: any) => i === node.data.sapId))
                }
            );
        };

    const onSelectionChanged = (event: SelectionChangedEvent) => {
        let selectedRows = gridRef.current!.api.getSelectedRows();
        setSelectedItemsInternal(selectedRows.map(s => s.sapId));
    };

    const onGridReady = useCallback((params: any) => {
        var defaultSortModel = [
            {colId: 'name', sort: 'asc'}
        ];
        params.columnApi.applyColumnState({state: defaultSortModel});
    }, [])

    const doesExternalFilterPass = (node: RowNode) => {
        if (!node.data) return true;
        for (let key in selectedFilter) {
            const selectedValues = selectedFilter[key];
            if (!selectedValues || selectedValues.length === 0) {
                continue;
            }
            const value = node.data[key];
            return !(value === undefined || !selectedValues.includes(value));
        }
        return true;
    };

    const toolbar = {
        renderInfo: () => <Typography>
            {t('selectDms.selectedItems', {numberOfItems: gridRef.current && gridRef.current.api ? gridRef.current.api.getSelectedRows().length : 0})}
        </Typography>
    }

    return (
        <Dialog scroll={"paper"} maxWidth={"lg"} disableEscapeKeyDown fullWidth={true} open={props.open}
                onClose={() => {
                    return;
                }}>
            <DialogTitle>
                <Typography fontWeight={"bold"} color={"primary"}>
                    {t('selectDms.title')}
                </Typography>
                <Typography color={"primary"}>
                    {t('selectDms.subTitle')}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => {
                        props.handleClose();
                    }}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <CloseRounded/>
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Paper sx={{padding: "0px 16px 16px", marginTop: "18px"}}
                       elevation={1}>
                    {areas.length > 0 &&
                        <Grid container spacing={2} sx={{paddingTop: "15px"}} alignItems={"center"}
                              justifyContent={"flex-start"}>
                            <Grid item xs={2.8}>
                                <Autocomplete
                                    fullWidth
                                    disabled={selectedFilter.area.length > 0}
                                    options={areas}
                                    multiple
                                    value={selectedFilter.area}
                                    onChange={handleChangeSelectedAreas}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="standard"
                                            label={t('selectDms.area')}
                                            placeholder={t('selectDms.area')}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>}
                    <Grid container spacing={2} alignItems={"flex-end"}>
                        <Grid item xs={2.8}>
                            <Autocomplete
                                fullWidth
                                disabled={selectedFilter.area.length > 0}
                                options={countries}
                                multiple
                                value={selectedFilter.country}
                                onChange={handleChangeSelectedCountries}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        label={t('selectDms.country')}
                                        placeholder={t('selectDms.country')}
                                    />
                                )}
                            />
                        </Grid>
                        {regions.length > 0 &&
                            <Grid item xs={2.8}>
                                <Autocomplete
                                    fullWidth
                                    disabled={selectedFilter.area.length > 0}
                                    options={regions}
                                    multiple
                                    value={selectedFilter.region}
                                    onChange={handleChangeSelectedRegions}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="standard"
                                            label={t('selectDms.region')}
                                            placeholder={t('selectDms.region')}
                                        />
                                    )}
                                />
                            </Grid>}
                        {provinces.length > 0 &&
                            <Grid item xs={2.8}>
                                <Autocomplete
                                    fullWidth
                                    disabled={selectedFilter.area.length > 0}
                                    options={provinces}
                                    multiple
                                    value={selectedFilter.province}
                                    onChange={handleChangeSelectedProvinces}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="standard"
                                            label={t('selectDms.province')}
                                            placeholder={t('selectDms.province')}
                                        />
                                    )}
                                />
                            </Grid>}
                        <Grid item xs={2.8}>
                            <Autocomplete
                                fullWidth
                                disabled={selectedFilter.area.length > 0}
                                options={cities}
                                multiple
                                value={selectedFilter.city}
                                onChange={handleChangeSelectedCities}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        label={t('selectDms.city')}
                                        placeholder={t('selectDms.city')}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={0.8}>
                            <IconButton color={"accent"} onClick={() => setSelectedFilter(
                                {
                                    area: [],
                                    country: [],
                                    region: [],
                                    province: [],
                                    city: []
                                }
                            )}>
                                <ReplayRounded/>
                            </IconButton>
                        </Grid>
                    </Grid>
                </Paper>
                <Grid container sx={{paddingTop: "16px"}}>

                    <Grid item xs={12}>
                        <AgGridCustomTable gridOptions={gridOptions} gridRef={gridRef} rowData={items}
                                           pagination={false}
                                           rowSelection={'multiple'} onFirstDataRendered={onFirstDataRendered}
                                           onSelectionChanged={onSelectionChanged}
                                           animateRows={true}
                                           isExternalFilterPresent={() => true}
                                           doesExternalFilterPass={doesExternalFilterPass}
                                           toolbar={toolbar}
                                           onGridReady={onGridReady}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant={"outlined"} color={"accent"}
                        onClick={() => {
                            props.handleClose()
                        }}>
                    {t('buttons.cancel')}
                </Button>
                <Button color={"accent"}
                        onClick={() => {
                            props.setSelectedItems(selectedItemsInternal);
                            props.handleClose()
                        }}>{t('buttons.save')}</Button>
            </DialogActions>
        </Dialog>
    )
}

export default AdvancedDMSselectionModal;